import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from './hooks/useTheme'
import { BREAKPOINTS, MOTION } from './lib/constants'
import { firstNameOf } from './lib/userName'
import { useAuth } from './hooks/useAuth'
import { useHousehold } from './hooks/useHousehold'
import { useItems } from './hooks/useItems'
import { useRecipePhotos, useRecipes } from './hooks/useRecipes'
import { useBilling } from './hooks/useBilling'
import { usePrices } from './hooks/usePrices'
import { useWatch } from './hooks/useWatch'
import { useForgetItems, useItemHistory } from './hooks/useItemHistory'
import { useDataExport } from './hooks/useDataExport'
import { useUndoDelete } from './hooks/useUndoDelete'
import { useApplyChoices } from './hooks/useApplyChoices'
import { useLayout } from './hooks/useLayout'
import { useMealPlan } from './hooks/useMealPlan'
import { useMealTemplates } from './hooks/useMealTemplates'
import { useCookingSessions } from './hooks/useCookingSessions'
import { CookingMode } from './components/cooking/CookingMode'
import type { CookState } from './lib/cooking'
import { dayKey, mondayOf, addDays } from './lib/mealPlan'
import { sharedLinkFrom } from './lib/recipeImport'
import { resolveCategoryId } from './lib/autocategory'
import { tintVar } from './lib/categoryTint'
import { knownNames } from './lib/catalog'
import { UndoToast } from './components/list/UndoToast'
import { freeItemsLeft, hasPlus, hasSubscription, type PlusReason } from './lib/billing'
import { LoginPage } from './components/LoginPage'
import { Onboarding } from './components/Onboarding'
import { GroceryList } from './components/GroceryList'
import { AppShell, type Tab } from './components/shell/AppShell'
import { RecipesTab } from './components/recipes/RecipesTab'
import { RecipeEditor } from './components/recipes/RecipeEditor'
import { RecipeDetail } from './components/recipes/RecipeDetail'
import { AnimatePresence } from 'framer-motion'
import { allTags, coverModel, recipeSource } from './lib/recipeBook'
import { lastTrip as findLastTrip } from './lib/lastTrip'
import { TripSheets, type TripSheet } from './components/stats/TripSheets'
import { MealPlanTab } from './components/meals/MealPlanTab'
import { CategorySheet } from './components/CategorySheet'
import { SettingsSheet } from './components/settings/SettingsSheet'
import { GetStarted } from './components/list/GetStarted'
import { SheetPanel } from './components/SheetPanel'
import { getStartedSteps, showGetStarted } from './lib/getStarted'
import { isStandalone } from './lib/standalone'
import { usePrefs } from './hooks/usePrefs'
import { setPref } from './lib/prefsStore'
import { useInstallPrompt } from './hooks/useInstallPrompt'
import { StatsSheet } from './components/stats/StatsSheet'
import { HomeTab } from './components/home/HomeTab'
import { buildStats } from './lib/stats'
import { useTrips } from './hooks/useTrips'
import { useShopping, useStores } from './hooks/useShopping'
import { memberName, shopLabel } from './lib/sharedTrip'
import { TripToast } from './components/shopping/TripToast'
import { ItemHistorySheet } from './components/settings/ItemHistorySheet'
import { SubscribeSheet } from './components/SubscribeSheet'
import { Splash } from './components/Wordmark'
import { useFirstRun } from './hooks/useFirstRun'
import { useTour } from './hooks/useTour'
import { questionsFor, stageFor } from './lib/firstRun'
import { userLocale } from './lib/locale'
import { IntroPager } from './components/firstRun/IntroPager'
import { PersonalizeFlow } from './components/firstRun/PersonalizeFlow'
import { Coachmark } from './components/ui/Coachmark'

const PAIR_INTENT_KEY = 'matlist.pairWatch'
/** A link shared into the app, parked across a sign-in like the pairing intent. */
const SHARED_LINK_KEY = 'matlist.sharedLink'

export default function App() {
  const { user, loading: authLoading, ...auth } = useAuth()
  const { signOut } = auth
  const {
    household, member, members, loading: householdLoading, error,
    createHousehold, joinHousehold, switchHousehold, renameHousehold, removeMember, resetInviteCode,
  } = useHousehold(user?.id ?? null)
  // A new account's first run (lib/firstRun.ts): the intro, setup, the
  // questions, then the tour. Accounts from before it are 'done' throughout.
  const { firstRun, save: saveFirstRun } = useFirstRun(user)
  const stage = stageFor(firstRun, !!household)
  const [showSettings, setShowSettings] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [showStart, setShowStart] = useState(false)
  const [showSubscribe, setShowSubscribe] = useState(false)
  // What the tap that opened the subscribe sheet was after (null: nothing in particular).
  const [plusReason, setPlusReason] = useState<PlusReason | null>(null)
  const upgrade = useCallback((reason?: PlusReason) => {
    setPlusReason(reason ?? null)
    setShowSubscribe(true)
  }, [])
  // Paid in the subscribe sheet's embedded form: it stays open on its
  // confirmation even after the webhook makes the household paying.
  const [paidHere, setPaidHere] = useState(false)
  const [showCategories, setShowCategories] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  // The app always opens on the list: shopping is what it's for.
  // The phone opens on Home (switched to the list below while a trip is open);
  // desktop has no Home and opens on the list.
  const [tab, setTab] = useState<Tab>(() =>
    (window.matchMedia?.(`(min-width: ${BREAKPOINTS.desktop}px)`).matches ? 'list' : 'home'))
  const [tabTouched, setTabTouched] = useState(false)
  const changeTab = useCallback((t: Tab) => { setTabTouched(true); setTab(t) }, [])
  // Home's Start shopping: the list opens and starts shopping there (GroceryList).
  const [shopRequested, setShopRequested] = useState(false)
  const shopRequestDone = useCallback(() => setShopRequested(false), [])
  // Home's End trip asks on the list, where the finish lives.
  const [endRequested, setEndRequested] = useState(false)
  const endRequestDone = useCallback(() => setEndRequested(false), [])
  // Home's recipe tile: Recipes opens Add a recipe (with what Home's Paste read).
  const [addRecipeRequest, setAddRecipeRequest] = useState<{ text?: string } | null>(null)
  // The recipe open in its detail, and the servings it opened at (a planned meal's).
  const [openRecipe, setOpenRecipe] = useState<{ id: string; servings?: number } | null>(null)
  // The Plan tab's week, in weeks from this one.
  const [weekOffset, setWeekOffset] = useState(0)
  // What is being cooked, and where its cooking stood when opened: a planned
  // meal (the Plan tab) or a recipe at the servings its page showed. Sessions
  // are kept per meal, and per recipe under "recipe:<id>".
  const [cooking, setCooking] = useState<
    { mealId: string; initial: CookState } | { recipeId: string; servings: number; initial: CookState } | null
  >(null)
  const sessions = useCookingSessions()
  // The watch's QR code (?pair, watch/gen_pair_qr.py) means the phone is
  // here for a pairing code. Read once at mount — from the URL, or from the
  // copy parked in localStorage by an earlier visit that was signed out — and
  // held until the Settings panel it opens is closed.
  const [pairIntent, setPairIntent] = useState(() => {
    if (new URLSearchParams(window.location.search).has('pair')) return true
    try { return localStorage.getItem(PAIR_INTENT_KEY) === '1' } catch { return false }
  })

  // A link shared in (Android's share sheet, ?import=): read once, parked so a
  // sign-in in between doesn't lose it, and imported on the Recipes tab.
  const [sharedLink, setSharedLinkState] = useState<string | null>(() => {
    const fromUrl = sharedLinkFrom(new URLSearchParams(window.location.search))
    try {
      if (fromUrl) localStorage.setItem(SHARED_LINK_KEY, fromUrl)
      return fromUrl ?? localStorage.getItem(SHARED_LINK_KEY)
    } catch { return fromUrl }
  })
  const setSharedLink = (link: string | null) => {
    setSharedLinkState(link)
    try { if (!link) localStorage.removeItem(SHARED_LINK_KEY) } catch { /* private mode */ }
  }

  const theme = useTheme()
  // The cold-start splash (ux/03 Splash): drawn over whatever renders first,
  // for one MOTION.splash.totalMs, then faded away. Non-interactive throughout.
  const [splash, setSplash] = useState<'on' | 'leaving' | 'off'>('on')
  useEffect(() => {
    const leave = setTimeout(() => setSplash('leaving'), MOTION.splash.totalMs - 250)
    const off = setTimeout(() => setSplash('off'), MOTION.splash.totalMs)
    return () => { clearTimeout(leave); clearTimeout(off) }
  }, [])

  const {
    items, categories, isLoading, addItem, toggleItem, deleteItem, updateItem, restoreItem, addCategory, renameCategory,
    deleteCategory, reorderCategories, finishBasket, finishItems, unsaved, discardUnsaved, retryUnsaved,
  } = useItems(household?.id ?? '', member?.id ?? '', { onListFull: () => upgrade('list_full') })

  const { recipes, importRecipe, importText, updateRecipe, saveRecipe, refreshRecipe, deleteRecipe } =
    useRecipes(household?.id ?? '', member?.id ?? '', { onPlusRequired: () => upgrade('recipes') })
  const photoUrls = useRecipePhotos(recipes)
  // The recipe editor: a new recipe (id null) or the one being changed.
  const [editing, setEditing] = useState<{ id: string | null; sourceText?: string } | null>(null)

  // For the Recipes and Plan tabs: where an ingredient would go on the list
  // (the household's own memory, as quick add uses), and its tint.
  const { memory, spellingFor, catalog, groups: historyGroups, loaded: historyLoaded, rows: historyRows } = useItemHistory(household?.id ?? '', items)
  const forgetItems = useForgetItems(household?.id ?? '')
  const categoryIds = useMemo(() => new Set(categories.map(c => c.id)), [categories])
  const categoryFor = useCallback((name: string) => resolveCategoryId(name, memory, categories), [memory, categories])
  const byId = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const tintFor = useCallback((id: string | null) => tintVar(id ? byId.get(id) : null), [byId])
  const known = useMemo(() => knownNames(items, catalog), [items, catalog])
  // Adding a recipe's or the week's ingredients: one toast with one Undo, on
  // the tab it came from (the List tab has its own for single rows).
  const tabUndo = useUndoDelete(items, deleteItem, restoreItem)
  const applyChoices = useApplyChoices({
    items, addItem, updateItem, deleteItem, toggleItem, categoryFor, categoryIds, offerUndo: tabUndo.bulkWithUndo,
  })
  const layout = useLayout()

  const today = new Date()
  const thisMonday = dayKey(mondayOf(today))
  const shownMonday = dayKey(addDays(mondayOf(today), weekOffset * 7))
  const mealPlan = useMealPlan(household?.id ?? '', member?.id ?? '', shownMonday, { onPlusRequired: () => upgrade('planning') })
  // This week's count for the tab, whichever week is shown (the same query when it's this one).
  const mealTemplates = useMealTemplates(household?.id ?? '', member?.id ?? '', { onPlusRequired: () => upgrade('planning') })
  const thisWeek = useMealPlan(household?.id ?? '', member?.id ?? '', thisMonday)
  const { prefs } = usePrefs()
  const { trips: householdTrips, loaded: tripsLoaded } = useTrips(household?.id ?? '', layout === 'phone')
  const {
    role: tripRole, joinMemberId, loaded: myTripsLoaded, notice: tripNotice, markSeen: markTripSeen, start: startTrip,
    addStore,
  } = useShopping(household?.id ?? '', member?.id ?? null)

  // This person's trip open when the app opens opens the list, not Home
  // (someone else's shows as Join on Home): decided once, when the trips have
  // loaded, and kept, so finishing the trip doesn't throw them back to Home.
  // Desktop has no Home.
  const [tripChecked, setTripChecked] = useState(false)
  // (Trips count as read only once the member is known: before that the hook says loaded with none.)
  if (!tripChecked && member && myTripsLoaded) {
    setTripChecked(true)
    if (tripRole === 'mine' && tab === 'home' && !tabTouched) setTab('list')
  }
  const shownTab: Tab = tab === 'home' && layout === 'desktop' ? 'list' : tab

  // Home's facts, from what the tabs already read: Stats' numbers, tonight's
  // meal, the last trip (and its summary, opened from the card).
  const homeStats = useMemo(
    () => (historyLoaded && tripsLoaded ? buildStats(historyRows, items, householdTrips, new Date()) : null),
    [historyLoaded, tripsLoaded, historyRows, items, householdTrips],
  )
  const todayKey = dayKey(today)
  const tonight = useMemo(() => {
    const meal = thisWeek.meals.find(m => m.day === todayKey && !m.cooked) ?? thisWeek.meals.find(m => m.day === todayKey)
    const recipe = meal && recipes.find(r => r.id === meal.recipe_id)
    return meal && recipe
      ? {
          mealId: meal.id, title: recipe.title, servings: meal.servings, minutes: recipe.minutes, cooked: meal.cooked,
          cover: coverModel(recipe, categoryFor, categories), photoUrl: photoUrls.get(recipe.id),
        }
      : null
  }, [thisWeek.meals, recipes, todayKey, categoryFor, categories, photoUrls])
  const stores = useStores(layout === 'phone' || stage === 'personalize' ? household?.id ?? '' : '')
  const homeLastTrip = useMemo(
    () => (historyLoaded && tripsLoaded
      ? findLastTrip(householdTrips, historyRows, id => (id ? stores.find(st => st.id === id)?.name ?? null : null))
      : undefined),
    [historyLoaded, tripsLoaded, householdTrips, historyRows, stores],
  )
  // Home's last trip: its summary, or Edit trip straight away from Add cost.
  const [homeTrip, setHomeTrip] = useState<TripSheet | null>(null)

  const billing = useBilling()
  const watch = useWatch(user?.id ?? null)
  const dataExport = useDataExport(household?.id ?? '')
  // The Get started card's own switches (this device) and Android's install prompt.
  const installPrompt = useInstallPrompt()
  // Fetched once here, above the auth gate: the login page quotes the price too.
  const prices = usePrices()

  // Strip the ?billing=success|cancel redirect param from the Checkout/Portal
  // round trip so it doesn't linger in the URL — the actual unlock is driven
  // by the Stripe webhook flipping subscription_status, which arrives to an
  // open tab live via the households realtime subscription in useHousehold,
  // not by this param.
  //
  // ?pair goes the same way, but is parked in localStorage first: the person
  // scanning the watch is usually signed out, and signing up (an emailed code,
  // possibly typed in after a reload) or signing in may not happen in this
  // same page load, so the param itself would not survive to the signed-in render.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const shared = ['import', 'url', 'text', 'title'].filter(k => params.has(k))
    if (!params.has('billing') && !params.has('pair') && !shared.length) return
    if (params.has('pair')) {
      try { localStorage.setItem(PAIR_INTENT_KEY, '1') } catch { /* private mode: the tap still works */ }
    }
    params.delete('billing')
    params.delete('pair')
    shared.forEach(k => params.delete(k))
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])

  // Once a signed-in member of a list is looking, the intent has been acted
  // on (the sheet below is open): unpark it, so a refresh doesn't open it
  // again. The in-memory flag lives on until the sheet is closed.
  const pairing = pairIntent && !!user && !!household
  useEffect(() => {
    if (!pairing) return
    try { localStorage.removeItem(PAIR_INTENT_KEY) } catch { /* nothing parked */ }
  }, [pairing])

  // A shared link waits for a signed-in member of a list, then opens Recipes, which imports it.
  const sharing = !!sharedLink && !!user && !!household
  const [sharedShown, setSharedShown] = useState<string | null>(null)
  if (sharing && sharedShown !== sharedLink) {
    setSharedShown(sharedLink)
    setTab('recipes')
  }

  // Get started (lib/getStarted.ts): a button beside Stats until the household
  // is set up, once the list and its history have loaded so nothing flashes.
  const startSteps = getStartedSteps({
    members: members.length, installed: isStandalone(), phone: layout === 'phone',
    hasItems: items.length > 0 || catalog.length > 0, recipes: recipes.length,
    watchPaired: !!watch.pairedAt, watchStep: prefs.watchStep,
  })
  const startShown = !isLoading && historyLoaded && showGetStarted(startSteps, prefs.getStarted)

  // The tour, on the real screens, once the questions are answered.
  const tour = useTour({
    firstRun, save: saveFirstRun, layout, getStarted: startShown,
    snapshot: { tab: shownTab, items: items.length },
  })
  // Someone who joined a list finds others on it; a list just made has one member.
  const joined = firstRun?.joined ?? members.length > 1

  const overlay = splash !== 'off' && <Splash overlay leaving={splash === 'leaving'} />

  if (authLoading || (user && householdLoading)) {
    return <Splash />
  }

  if (!user) {
    return <>{overlay}<LoginPage auth={auth} prices={prices} pairIntent={pairIntent} /></>
  }

  if (!household && stage === 'intro' && !pairIntent) {
    return <>{overlay}<IntroPager onDone={() => saveFirstRun({ stage: 'setup' })} /></>
  }

  if (!household) {
    return (
      <>
        {overlay}
        {/* In a first run it sits on the intro's ink, so the two read as one. */}
        <div className={firstRun ? 'ml-dark-scope' : undefined}>
          <Onboarding
            suggestedName={firstNameOf(user)}
            onCreate={createHousehold}
            onJoin={joinHousehold}
            loading={householdLoading}
            error={error}
          />
        </div>
      </>
    )
  }

  if (stage === 'personalize') {
    const open = items.filter(i => !i.done)
    const aisles = categories.filter(c => open.some(i => i.category_id === c.id))
      .map(c => ({ name: c.name, tint: tintFor(c.id) }))
    return (
      <>
        {overlay}
        <PersonalizeFlow
          questions={questionsFor(joined)} locale={userLocale()} joined={joined}
          householdName={household.name} inviteCode={household.invite_code}
          onList={open.map(i => i.name)} stores={stores.map(st => st.name)}
          summary={{ count: open.length, aisles }}
          tintOf={name => tintFor(categoryFor(name))}
          onAddItems={names => names.forEach(name => addItem(name, categoryFor(name)))}
          onAddStores={names => names.forEach(name => { void addStore(name) })}
          onWatch={wears => setPref('watchStep', wears)}
          onDone={(answers, pair) => {
            saveFirstRun({ ...answers, joined, stage: 'tour' })
            setTab(layout === 'phone' ? 'home' : 'list')
            if (pair) setPairIntent(true)
          }}
        />
      </>
    )
  }

  // Free (042_free_plus.sql) keeps the list, categories and shopping; recipes,
  // meal planning, the watch, Stats and the household's memory are Plus. Each
  // Plus write is guarded once, here, with the reason the sheet leads with —
  // so a tap opens the subscribe sheet instead of a row flashing and rolling
  // back. RLS refuses the write regardless; onPlusRequired above covers a
  // trial that ends while the tab is open. The list's own limit (15 open items)
  // is GroceryList's, and the server's trigger.
  const plus = hasPlus(household)
  const requirePlus = <A extends unknown[], R>(reason: PlusReason, fn: (...args: A) => R, refused: R) =>
    (...args: A): R => {
      if (plus) return fn(...args)
      upgrade(reason)
      return refused
    }

  const counts = { list: items.filter(i => !i.done).length, recipes: recipes.length, meals: thisWeek.meals.length }

  // Get started opens on its own (desktop's sidebar row) or above Stats (the
  // phone's one button); its actions close whichever it is in.
  const closeStart = () => { setShowStart(false); setShowStats(false) }
  const getStarted = (onClose?: () => void) => (
    <GetStarted steps={startSteps} householdName={household.name} inviteCode={household.invite_code}
      phone={layout === 'phone'} installPrompt={installPrompt}
      onHide={() => { setPref('getStarted', false); closeStart() }}
      onNoWatch={() => setPref('watchStep', false)}
      onPairWatch={() => { closeStart(); setPairIntent(true) }}
      onRecipes={() => { closeStart(); setTab('recipes') }}
      onOpenSettings={() => { closeStart(); setShowSettings(true) }}
      onClose={onClose} />
  )

  return (
    <>
      {overlay}
      <AppShell tab={shownTab} onTab={changeTab} counts={counts} members={members} onOpenSettings={() => setShowSettings(true)}
        onOpenStats={() => setShowStats(true)}
        start={startShown ? { done: startSteps.filter(s => s.done).length, total: startSteps.length, onOpen: () => setShowStart(true) } : undefined}>
      {shownTab === 'home' && <HomeTab
        members={members}
        selfMemberId={member?.id ?? null}
        stats={homeStats}
        now={today}
        open={items.filter(i => !i.done)}
        tripOpen={tripRole !== 'none'}
        shopLabel={shopLabel(tripRole, memberName(members, joinMemberId))}
        tonight={tonight}
        lastTrip={homeLastTrip}
        freeLeft={plus ? null : freeItemsLeft(household, counts.list)}
        tintFor={tintFor}
        nameOf={id => (id && byId.get(id)?.name) || 'Other'}
        plus={plus}
        onOpen={changeTab}
        onStartShopping={() => { changeTab('list'); setShopRequested(true) }}
        onEndTrip={tripRole === 'join' ? () => { changeTab('list'); setEndRequested(true) } : undefined}
        onCook={mealId => setCooking({ mealId, initial: sessions.open(mealId, Date.now()) })}
        onOpenTrip={() => {
          if (homeLastTrip) setHomeTrip({ summary: { ...homeLastTrip, askCost: !homeLastTrip.finishedBy || homeLastTrip.finishedBy === member?.id } })
        }}
        onAddCost={() => { if (homeLastTrip?.tripId) setHomeTrip({ tripId: homeLastTrip.tripId }) }}
        onUpgrade={() => upgrade('planning')}
        onPasteRecipe={text => { changeTab('recipes'); setAddRecipeRequest({ text }) }}
        onAddRecipe={() => { changeTab('recipes'); setAddRecipeRequest({}) }}
      />}
      {shownTab === 'list' && <GroceryList
        shopRequested={shopRequested}
        onShopRequested={shopRequestDone}
        endRequested={endRequested}
        onEndRequested={endRequestDone}
        household={household}
        items={items}
        categories={categories}
        members={members}
        selfMemberId={member?.id ?? null}
        isLoading={isLoading}
        onToggle={toggleItem}
        onDelete={deleteItem}
        onRestore={restoreItem}
        onAdd={addItem}
        onUpdate={updateItem}
        onFinishBasket={finishBasket}
        onFinishStale={finishItems}
        unsaved={unsaved}
        onDiscardUnsaved={discardUnsaved}
        onRetryUnsaved={retryUnsaved}
        onUpgrade={upgrade}
      />}
      {shownTab === 'recipes' && <RecipesTab
        recipes={recipes} items={items} members={members} categories={categories}
        locked={!plus} categoryFor={categoryFor} spellingFor={spellingFor} before={catalog} tintFor={tintFor}
        importRecipe={importRecipe} importText={importText} updateRecipe={requirePlus('recipes', updateRecipe, undefined)}
        onOpen={id => setOpenRecipe({ id })}
        onEditRecipe={(id, sourceText) => setEditing({ id, sourceText })}
        sharedLink={sharing ? sharedLink : null} onSharedLinkUsed={() => setSharedLink(null)}
        addRequest={addRecipeRequest} onAddRequestUsed={() => setAddRecipeRequest(null)}
        onNewRecipe={() => setEditing({ id: null })}
        photoUrls={photoUrls}
        onApply={requirePlus('recipes', (needs, choices, note) => { applyChoices(needs, choices, note) }, undefined)}
        onUpgrade={() => upgrade('recipes')}
      />}
      {shownTab === 'meals' && <MealPlanTab
        today={today} weekOffset={weekOffset} meals={mealPlan.meals}
        recipes={recipes} items={items} categories={categories}
        categoryFor={categoryFor} spellingFor={spellingFor} before={catalog} tintFor={tintFor} locked={!plus}
        photoUrls={photoUrls}
        onWeek={setWeekOffset}
        onAddMeal={requirePlus('planning', mealPlan.addMeal, '')}
        onRemoveMeal={requirePlus('planning', mealPlan.removeMeal, undefined)}
        onServings={requirePlus('planning', mealPlan.setServings, undefined)}
        onOpenRecipe={(id, servings) => setOpenRecipe({ id, servings })}
        onCook={mealId => setCooking({ mealId, initial: sessions.open(mealId, Date.now()) })}
        onApply={requirePlus('planning', (needs, choices, note, mealIds) => {
          applyChoices(needs, choices, note, () => mealPlan.markAdded(mealIds, false))
          mealPlan.markAdded(mealIds, true)
        }, undefined)}
        templates={mealTemplates.templates}
        onSaveTemplate={requirePlus('planning', (name, meals) => { mealTemplates.saveTemplate(name, meals) }, undefined)}
        onDeleteTemplate={requirePlus('planning', mealTemplates.deleteTemplate, undefined)}
        onPlanMeals={requirePlus('planning', (meals, label) => {
          const ids = meals.map(m => mealPlan.addMeal(m.day, m.recipe_id, m.servings))
          tabUndo.bulkWithUndo(`Planned ${ids.length} ${ids.length === 1 ? 'meal' : 'meals'} from “${label}”`,
            () => ids.forEach(id => mealPlan.removeMeal(id)))
        }, undefined)}
        onUpgrade={() => upgrade('planning')}
      />}
      {shownTab !== 'list' && <div className="ml-tab-toast"><UndoToast pending={tabUndo.pending} layout={layout} onUndo={tabUndo.undo} /></div>}
      {tour.step && (
        <Coachmark key={tour.step.id} step={tour.step} number={tour.number} total={tour.total} onNext={tour.next} onSkip={tour.skip} />
      )}
      <TripToast notice={tripNotice} members={members} phone={layout === 'phone'} onSeen={markTripSeen}
        onKeepShopping={t => { startTrip(t.store_id); changeTab('list'); setShopRequested(true) }} />
      </AppShell>
      <AnimatePresence>
        {(() => {
          const recipe = openRecipe && recipes.find(r => r.id === openRecipe.id)
          if (!recipe) return null
          return (
            <RecipeDetail key={recipe.id}
              recipe={recipe} items={items} householdTags={allTags(recipes)} categories={categories}
              categoryFor={categoryFor} spellingFor={spellingFor} before={catalog} tintFor={tintFor} source={recipeSource(recipe, members)}
              initialServings={openRecipe.servings} locked={!plus}
              onUpdate={patch => updateRecipe(recipe.id, patch)}
              onDelete={() => deleteRecipe(recipe.id)}
              onEdit={() => setEditing({ id: recipe.id })}
              onRefresh={() => refreshRecipe(recipe.id)}
              onCook={servings => {
                const key = `recipe:${recipe.id}`
                setOpenRecipe(null)
                setCooking({ recipeId: recipe.id, servings, initial: sessions.open(key, Date.now()) })
              }}
              photoUrl={photoUrls.get(recipe.id)}
              onApply={requirePlus('recipes', (needs, choices, note) => { applyChoices(needs, choices, note) }, undefined)}
              onUpgrade={() => upgrade('recipes')}
              onClose={() => setOpenRecipe(null)}
            />
          )
        })()}
      </AnimatePresence>
      {(() => {
        if (cooking && 'recipeId' in cooking) {
          const recipe = recipes.find(r => r.id === cooking.recipeId)
          if (!recipe) return null
          const key = `recipe:${recipe.id}`
          return (
            <CookingMode recipe={recipe} servings={cooking.servings} initial={cooking.initial} planned={false}
              onSession={s => sessions.save(key, s)}
              onClose={() => setCooking(null)}
              onCooked={() => { sessions.drop(key); setCooking(null) }} />
          )
        }
        const meal = cooking && mealPlan.meals.find(m => m.id === cooking.mealId)
        const recipe = meal && recipes.find(r => r.id === meal.recipe_id)
        if (!cooking || !meal || !recipe) return null
        return (
          <CookingMode recipe={recipe} servings={meal.servings} initial={cooking.initial}
            onSession={s => sessions.save(meal.id, s)}
            onClose={() => setCooking(null)}
            onCooked={() => {
              setCooking(null)
              if (!plus) { upgrade('planning'); return }
              sessions.drop(meal.id)
              mealPlan.markCooked(meal.id)
              tabUndo.bulkWithUndo('Marked as cooked', () => mealPlan.markCooked(meal.id, false))
            }} />
        )
      })()}
      {(() => {
        const recipe = editing?.id ? recipes.find(r => r.id === editing.id) : null
        return (
          <RecipeEditor open={!!editing && (editing.id === null || !!recipe)}
            recipe={recipe} photoUrl={recipe ? photoUrls.get(recipe.id) : null} sourceText={editing?.sourceText}
            categories={categories} categoryFor={categoryFor} tintFor={tintFor} known={known}
            onSave={(fields, photo) => saveRecipe(editing?.id ?? null, fields, photo)}
            onSaved={id => { if (!editing?.id) setOpenRecipe({ id }) }}
            onClose={() => setEditing(null)} />
        )
      })()}
      <CategorySheet
        open={showCategories}
        categories={categories}
        countOf={id => items.filter(i => i.category_id === id).length}
        tintFor={tintFor}
        onAdd={name => addCategory(name, null)}
        onRename={renameCategory}
        onDelete={deleteCategory}
        onReorder={reorderCategories}
        onClose={() => setShowCategories(false)}
      />
      <ItemHistorySheet
        open={showHistory}
        groups={historyGroups}
        loaded={historyLoaded}
        onForget={forgetItems}
        onClose={() => setShowHistory(false)}
      />
      <AnimatePresence>
        {showStart && startShown && (
          <SheetPanel onClose={closeStart} ariaLabel="Get started" maxHeight="92dvh" layout="scroll">
            {getStarted(closeStart)}
          </SheetPanel>
        )}
      </AnimatePresence>
      <TripSheets sheet={homeTrip} householdId={household.id} items={items} categories={categories}
        members={members} selfMemberId={member?.id ?? null} onClose={() => setHomeTrip(null)} />
      <StatsSheet open={showStats} top={startShown ? getStarted() : undefined} householdId={household.id} items={items} categories={categories}
        members={members} selfMemberId={member?.id ?? null} locked={!plus}
        onUpgrade={() => { setShowStats(false); upgrade('stats') }} onClose={() => setShowStats(false)} />

      <SettingsSheet
        open={showSettings || pairing}
        email={user.email ?? ''}
        household={household}
        self={member}
        members={members}
        watch={watch}
        prices={prices}
        theme={theme}
        billingLoading={billing.loading}
        onRename={renameHousehold}
        onRemoveMember={removeMember}
        onResetInviteCode={resetInviteCode}
        onSwitchHousehold={switchHousehold}
        onUpgrade={() => { setShowSettings(false); setPairIntent(false); upgrade() }}
        onManageSubscription={billing.openBillingPortal}
        onCategories={() => setShowCategories(true)}
        onItemHistory={() => setShowHistory(true)}
        onExport={dataExport.download}
        onSignOut={signOut}
        autoAddWatch={pairing}
        onClose={() => { setShowSettings(false); setPairIntent(false) }}
      />
      {/* `!hasSubscription` closes the sheet the moment the household pays —
          the webhook's UPDATE reaches the open tab through the households
          realtime subscription in useHousehold, so a family returning from
          hosted Checkout lands on the list. Paid in the embedded form, the
          sheet stays on its confirmation until closed. */}
      <SubscribeSheet
        open={showSubscribe && (!hasSubscription(household) || paidHere)}
        household={household}
        prices={prices}
        loading={billing.loading}
        error={billing.error}
        onCheckout={billing.startCheckout}
        session={billing.session}
        onBack={billing.cancelCheckout}
        onPaid={() => { setPaidHere(true); billing.cancelCheckout() }}
        paid={paidHere}
        reason={plusReason}
        onClose={() => { setShowSubscribe(false); setPaidHere(false); billing.cancelCheckout() }}
      />
    </>
  )
}
